/**
 * Infrastructure Layer - MedicalReservationProcessorImpl
 * 診療予約データ加工ポートの実装
 */

import type {
  MedicalReservationProcessor,
  ProcessRequest,
} from '../../application/medicalReservation/ports/MedicalReservationProcessor';
import type { ProcessingResult } from '../../domain/medicalReservation/ProcessingResult';
import { createProcessingResult } from '../../domain/medicalReservation/ProcessingResult';
import type { ProcessedReservation } from '../../domain/medicalReservation/MedicalReservation';
import {
  ProcessingErrorType,
  type ProcessingError,
  type Result,
  ok,
  err,
  createProcessingError,
} from '../../domain/medicalReservation/errors';
import { processCsvData } from '../api/medicalReservationApi';
import type { CsvProcessResponse } from '../../types/medicalReservationProcess';

/**
 * APIレスポンスからProcessedReservation配列に変換する
 */
const convertToProcessedReservations = (
  apiResponse: CsvProcessResponse
): ProcessedReservation[] => {
  const rows = apiResponse.data.csvData.rows;
  const processingLogs = apiResponse.data.processingLogs || [];

  // 処理ログをrowIndexでインデックス化
  const logsByRow = new Map<number, typeof processingLogs>();
  processingLogs.forEach((log) => {
    const logs = logsByRow.get(log.rowIndex) || [];
    logs.push(log);
    logsByRow.set(log.rowIndex, logs);
  });

  return rows.map((row, index) => {
    const rowLogs = logsByRow.get(index) || [];

    // ステータス変換ログを取得
    const statusLog = rowLogs.find((log) => log.action === 'status_conversion');
    // 医師割り当てログを取得
    const doctorLog = rowLogs.find((log) => log.action === 'doctor_assignment');

    const originalStatus = row['ステータス'] || '';
    const originalDoctor = row['担当医師'] || '';
    const processedStatus = originalStatus; // APIで変換済み
    const processedDoctor = originalDoctor; // APIで割り当て済み

    return {
      id: index,
      anybotId: row['anybot ID'] || '',
      consultationDateTime: row['診療開始時間'] || '',
      patientName: row['名前（漢字）'] || '',
      doctorName: processedDoctor,
      status: processedStatus,
      originalStatus,
      originalDoctorName: originalDoctor,
      isStatusConverted: statusLog?.result === 'success',
      isDoctorChanged: doctorLog?.result === 'success',
      rawData: Object.freeze({ ...row }),
    };
  });
};

/**
 * APIレスポンスからステータス変換ログエントリを抽出する
 */
const extractConversionLogEntries = (apiResponse: CsvProcessResponse) => {
  const logs = apiResponse.data.processingLogs || [];
  return logs
    .filter((log) => log.action === 'status_conversion')
    .map((log) => ({
      rowIndex: log.rowIndex,
      patientIdentifier: log.anybotId,
      originalStatus: '', // APIレスポンスから取得
      convertedStatus: '', // APIレスポンスから取得
      reason: log.details,
    }));
};

/**
 * APIレスポンスから医師割り当てログエントリを抽出する
 */
const extractAssignmentLogEntries = (apiResponse: CsvProcessResponse) => {
  const logs = apiResponse.data.processingLogs || [];
  return logs
    .filter((log) => log.action === 'doctor_assignment')
    .map((log) => ({
      rowIndex: log.rowIndex,
      patientIdentifier: log.anybotId,
      originalDoctor: '',
      assignedDoctor: '',
      reason: log.details,
    }));
};

/**
 * 診療予約データ加工ポートの実装
 */
export class MedicalReservationProcessorImpl
  implements MedicalReservationProcessor
{
  /**
   * CSVデータを加工する
   */
  async process(
    request: ProcessRequest
  ): Promise<Result<ProcessingResult, ProcessingError>> {
    try {
      // 時間範囲から開始・終了時刻を抽出
      const timeRangeStart = request.timeRange.startTime;
      const timeRangeEnd = request.timeRange.endTime;

      // APIを呼び出して処理
      const apiResponse = await processCsvData(
        request.headers,
        request.rows,
        timeRangeStart,
        timeRangeEnd
      );

      // APIレスポンスをドメインオブジェクトに変換
      const processedData = convertToProcessedReservations(apiResponse);
      const conversionEntries = extractConversionLogEntries(apiResponse);
      const assignmentEntries = extractAssignmentLogEntries(apiResponse);

      // サマリーを構築
      const summary = {
        totalCount: apiResponse.data.metadata.totalRows,
        successCount: apiResponse.data.metadata.processedRows,
        skippedCount: 0,
        errorCount: apiResponse.data.metadata.errorRows,
      };

      // ProcessingResultを生成
      const result = createProcessingResult(
        processedData,
        conversionEntries,
        assignmentEntries,
        summary
      );

      return ok(result);
    } catch (error) {
      // APIエラーをドメインエラーに変換
      const message =
        error instanceof Error
          ? error.message
          : 'データ加工処理に失敗しました';

      // エラータイプを判定
      let errorType: ProcessingErrorType = ProcessingErrorType.API_ERROR;
      if (message.includes('timeout') || message.includes('タイムアウト')) {
        errorType = ProcessingErrorType.TIMEOUT;
      } else if (
        message.includes('validation') ||
        message.includes('バリデーション')
      ) {
        errorType = ProcessingErrorType.VALIDATION_ERROR;
      }

      return err(
        createProcessingError(
          errorType,
          message,
          error instanceof Error ? error.stack : undefined
        )
      );
    }
  }
}

/**
 * MedicalReservationProcessorImplのファクトリ関数
 */
export const createMedicalReservationProcessor =
  (): MedicalReservationProcessor => {
    return new MedicalReservationProcessorImpl();
  };
